import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { clearTokens } from '@features/auth/model/slice';
import { refreshAccessToken } from '@features/auth/model/refresh';
import type { AppDispatch, RootState } from '@app/store';
import { parseResponse } from './parseResponse';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE,
  responseHandler: parseResponse,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Hard-redirect на /login когда session окончательно потеряна
 * (refresh-токен невалиден или истёк). Срабатывает один раз — иначе RTK
 * Query со множеством параллельных запросов триггерит redirect каждым.
 *
 * Почему именно hard-redirect, а не Navigate через React Router:
 * - clearTokens() обновляет state, но ProtectedShell делает Navigate
 *   только при ре-рендере. Если в момент 401-серии RTK Query держит
 *   in-flight запросы, ре-рендер может отложиться, юзер залипает на
 *   странице с 401 во всех запросах.
 * - window.location.replace гарантирует чистый старт — никакого state
 *   из старой сессии не утечёт.
 */
let redirectingToLogin = false;
function bailToLogin(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login?expired=1');
  }
}

/**
 * Перезапуск контейнера — это несколько секунд, когда nginx отвечает 502, а
 * сеть рвёт соединение. Для чтения это лечится повтором: один-два повтора с
 * паузой, и человек ничего не заметил. Повторяем ТОЛЬКО чтение — повторить
 * POST значит рискнуть создать запись дважды.
 *
 * Дольше тянуть смысла нет: полное обновление идёт около минуты, и про него
 * говорит баннер (`MaintenanceBanner`), а не бесконечный спиннер.
 */
const RETRY_DELAYS_MS = [1500, 3000];

function isTransient(error: FetchBaseQueryError | undefined): boolean {
  if (!error) return false;
  // Сеть не ответила вовсе — контейнер как раз поднимается.
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') return true;
  return error.status === 502 || error.status === 503 || error.status === 504;
}

function isRead(args: string | FetchArgs): boolean {
  if (typeof args === 'string') return true;
  const method = (args.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD';
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reactive fallback на 401: proactive хук должен покрывать 99% случаев,
 * но если по какой-то причине access истёк (часы клиента сильно сдвинуты,
 * был sleep до того как хук установился, etc.) — рефрешим здесь.
 *
 * Мьютекс на refresh живёт в `features/auth/model/refresh.ts` —
 * единственный источник истины. И proactive-хук, и этот fallback дёргают
 * один и тот же `refreshAccessToken`, гонок не будет.
 */
export const baseQueryWithRefresh: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (isRead(args)) {
    for (const delay of RETRY_DELAYS_MS) {
      if (!isTransient(result.error)) break;
      await sleep(delay);
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  if (result.error?.status === 401) {
    const refreshToken = (api.getState() as RootState).auth.refreshToken;
    if (!refreshToken) {
      api.dispatch(clearTokens());
      bailToLogin();
      return result;
    }

    const ok = await refreshAccessToken(
      api.dispatch as AppDispatch,
      api.getState as () => RootState,
    );
    if (ok) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(clearTokens());
      bailToLogin();
    }
  }

  return result;
};
